// Procedural texture generators for FX & Materials (docs/3d-content-
// pipeline/roadmap.md 3.2 Phase 4) - "in the spirit of sstk-sprite-kit's
// baked generators, reimplemented for the browser" per the roadmap's own
// framing, not a shared dependency with it (sstk-sprite-kit is build-time
// Node tooling, never imported by runtime code).
//
// Every generator returns a plain Uint8ClampedArray of RGBA bytes - pure,
// DOM-free, and unit-testable - rather than touching a `<canvas>` directly;
// jsdom (this repo's test environment) has no real 2D canvas context, and
// every other pixel-producing routine in this codebase (see editor-pixel.tsx's
// `decodeImageFile`/`sliceSpritesheetPixels`) draws the same line: the
// canvas is a thin, untested presentation detail, the pixel math is what
// gets tested. The caller (editor-data-fx.tsx) is responsible for painting
// the result into a canvas and encoding it for upload.
import { createNoise4D } from 'simplex-noise';

export interface TextureGeneratorParams {
  width: number;
  height: number;
  /** How many times the pattern repeats across the texture. */
  scale: number;
  /** -1..1; pushes the blend between colourA/colourB toward the extremes. */
  contrast: number;
  seed: number;
  colourA: string;
  colourB: string;
}

export type TextureGeneratorKind = 'noise' | 'gradient' | 'checker' | 'stripe' | 'brick';
export const TEXTURE_GENERATOR_KINDS: TextureGeneratorKind[] = ['noise', 'gradient', 'checker', 'stripe', 'brick'];

/** A small, dependency-free deterministic PRNG (mulberry32) so the same
 * seed always reproduces the same noise field - `simplex-noise`'s own
 * `createNoise4D` takes an injectable RandomFn for exactly this. */
function mulberry32(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Exported for reuse by texturePaint.ts (a brush colour is authored as a
 * hex swatch, same as colourA/colourB here) - keep this the one place a hex
 * colour becomes RGB bytes rather than growing a third copy. */
export function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const value = parseInt(clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/** Pushes `t` (0..1) away from 0.5 by `contrast` (-1..1) without leaving 0..1.
 * The slope (`1 + contrast * 2`) is clamped at 0 rather than left to go
 * negative: contrast=-1 should fully flatten `t` to the 0.5 midpoint, not
 * overshoot past it and invert colourA/colourB - a negative slope would
 * swap `t` and `1-t`, silently inverting the texture for roughly the
 * bottom half of the negative contrast range. */
function applyContrast(t: number, contrast: number): number {
  const slope = Math.max(0, 1 + contrast * 2);
  return clamp01(0.5 + (t - 0.5) * slope);
}

function fillFromMix(out: Uint8ClampedArray, width: number, height: number, colourA: string, colourB: string, mixAt: (x: number, y: number) => number): Uint8ClampedArray {
  const [r1, g1, b1] = hexToRgb(colourA);
  const [r2, g2, b2] = hexToRgb(colourB);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const t = clamp01(mixAt(x, y));
      const i = (y * width + x) * 4;
      out[i] = r1 + (r2 - r1) * t;
      out[i + 1] = g1 + (g2 - g1) * t;
      out[i + 2] = b1 + (b2 - b1) * t;
      out[i + 3] = 255;
    }
  }
  return out;
}

/**
 * Seamlessly tileable noise: rather than sampling 2D simplex noise directly
 * (which does not repeat at the texture's own edges), each pixel's (u, v)
 * texture coordinate is mapped onto a torus in 4D space - one full 2π loop
 * per axis - and 4D simplex noise is sampled at that point. Since the torus
 * itself is a closed loop, the sampled noise field is automatically
 * continuous (and therefore tileable) at every edge.
 */
export function generateNoiseTexture(params: TextureGeneratorParams): Uint8ClampedArray {
  const { width, height, scale, contrast, seed, colourA, colourB } = params;
  const noise4D = createNoise4D(mulberry32(seed));
  const out = new Uint8ClampedArray(width * height * 4);
  return fillFromMix(out, width, height, colourA, colourB, (x, y) => {
    const u = (x / width) * 2 * Math.PI;
    const v = (y / height) * 2 * Math.PI;
    const n = noise4D(Math.cos(u) * scale, Math.sin(u) * scale, Math.cos(v) * scale, Math.sin(v) * scale);
    return applyContrast((n + 1) / 2, contrast);
  });
}

/** A vertical linear gradient, top (colourA) to bottom (colourB). `scale`
 * repeats it that many times down the texture (1 = one smooth sweep). */
export function generateGradientTexture(params: TextureGeneratorParams): Uint8ClampedArray {
  const { width, height, scale, contrast, colourA, colourB } = params;
  const out = new Uint8ClampedArray(width * height * 4);
  return fillFromMix(out, width, height, colourA, colourB, (_x, y) => {
    const t = ((y / height) * Math.max(1, scale)) % 1;
    return applyContrast(t, contrast);
  });
}

/** A checkerboard, `scale` cells across the texture's width (and a matching
 * count down its height, so cells stay square). */
export function generateCheckerTexture(params: TextureGeneratorParams): Uint8ClampedArray {
  const { width, height, scale, contrast, colourA, colourB } = params;
  const cells = Math.max(1, Math.round(scale));
  const cellW = width / cells;
  const cellH = height / cells;
  const out = new Uint8ClampedArray(width * height * 4);
  return fillFromMix(out, width, height, colourA, colourB, (x, y) => {
    const cx = Math.floor(x / cellW);
    const cy = Math.floor(y / cellH);
    return applyContrast((cx + cy) % 2 === 0 ? 0 : 1, contrast);
  });
}

/** Vertical stripes, `scale` of them across the texture's width. */
export function generateStripeTexture(params: TextureGeneratorParams): Uint8ClampedArray {
  const { width, height, scale, contrast, colourA, colourB } = params;
  const cells = Math.max(1, Math.round(scale));
  const cellW = width / cells;
  const out = new Uint8ClampedArray(width * height * 4);
  return fillFromMix(out, width, height, colourA, colourB, (x) => applyContrast(Math.floor(x / cellW) % 2 === 0 ? 0 : 1, contrast));
}

/** Running-bond brickwork: `scale` rows of bricks, each row offset by half a
 * brick from the one above, with a thin mortar line (colourB) between
 * bricks (colourA). */
export function generateBrickTexture(params: TextureGeneratorParams): Uint8ClampedArray {
  const { width, height, scale, contrast, colourA, colourB } = params;
  const rows = Math.max(1, Math.round(scale));
  const brickH = height / rows;
  const bricksPerRow = rows * 2;
  const brickW = width / bricksPerRow;
  const mortar = Math.max(1, Math.min(brickW, brickH) * 0.08);
  const out = new Uint8ClampedArray(width * height * 4);
  return fillFromMix(out, width, height, colourA, colourB, (x, y) => {
    const row = Math.floor(y / brickH);
    const offset = (row % 2) * (brickW / 2);
    const withinRow = y % brickH;
    const withinCol = (x + offset) % brickW;
    const isMortar = withinRow < mortar || withinRow > brickH - mortar || withinCol < mortar || withinCol > brickW - mortar;
    return applyContrast(isMortar ? 1 : 0, contrast);
  });
}

export function generateTexture(kind: TextureGeneratorKind, params: TextureGeneratorParams): Uint8ClampedArray {
  switch (kind) {
    case 'gradient': return generateGradientTexture(params);
    case 'checker': return generateCheckerTexture(params);
    case 'stripe': return generateStripeTexture(params);
    case 'brick': return generateBrickTexture(params);
    case 'noise':
    default: return generateNoiseTexture(params);
  }
}

/**
 * A cheap normal map derived from an existing albedo/height image (3.2
 * Phase 4's "genuinely cheap bonus once Phase 1 ships"): treats the
 * albedo's own luminance as a height field and runs a 3x3 Sobel filter over
 * it to estimate the surface gradient at each pixel, then encodes
 * (-gx, -gy, 1) - normalised and remapped from -1..1 to 0..255 - as a
 * standard tangent-space normal map (flat = (128,128,255)). Edges sample
 * with clamping (nearest interior pixel) rather than wrapping, since an
 * uploaded photo/albedo isn't guaranteed tileable the way the generators
 * above are.
 */
export function generateNormalMapFromAlbedo(albedo: Uint8ClampedArray, width: number, height: number, strength = 1): Uint8ClampedArray {
  const luminanceAt = (x: number, y: number): number => {
    const cx = Math.min(width - 1, Math.max(0, x));
    const cy = Math.min(height - 1, Math.max(0, y));
    const i = (cy * width + cx) * 4;
    return (0.299 * albedo[i]! + 0.587 * albedo[i + 1]! + 0.114 * albedo[i + 2]!) / 255;
  };

  const out = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      // Sobel kernels.
      const gx =
        -luminanceAt(x - 1, y - 1) + luminanceAt(x + 1, y - 1) +
        -2 * luminanceAt(x - 1, y) + 2 * luminanceAt(x + 1, y) +
        -luminanceAt(x - 1, y + 1) + luminanceAt(x + 1, y + 1);
      const gy =
        -luminanceAt(x - 1, y - 1) - 2 * luminanceAt(x, y - 1) - luminanceAt(x + 1, y - 1) +
        luminanceAt(x - 1, y + 1) + 2 * luminanceAt(x, y + 1) + luminanceAt(x + 1, y + 1);

      const nx = -gx * strength;
      const ny = -gy * strength;
      const nz = 1;
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
      const i = (y * width + x) * 4;
      out[i] = ((nx / len) * 0.5 + 0.5) * 255;
      out[i + 1] = ((ny / len) * 0.5 + 0.5) * 255;
      out[i + 2] = ((nz / len) * 0.5 + 0.5) * 255;
      out[i + 3] = 255;
    }
  }
  return out;
}
